import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { isCacheStale, recordDataSync } from './cacheService';
import { openIndexedDBWithStores } from './indexedDBUtils';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Brand Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== BRAND DATA OPERATIONS =====

/**
 * Gets all brands with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of brand objects with equipment counts
 */
export const getAllBrands = async (forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      // Try to get from IndexedDB cache first but check cache freshness
      const cachedBrands = await getBrandsFromCache();
      
      if (cachedBrands && cachedBrands.length > 0) {
        // Check if cache is stale
        if (!isCacheStale('brands')) {
          conditionalLog(`Using cached brand data (${cachedBrands.length} items)`);
          
          // Deserialize dates from cached data
          const deserializedBrands = cachedBrands.map(brand => deserializeDates(brand));
          
          // Sort manually by name
          deserializedBrands.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
          
          return deserializedBrands;
        } else {
          conditionalLog('Cached brands are stale, fetching fresh data from Firestore...');
        }
      }
    }
    
    // Cache miss or force refresh - fetch from Firestore and update cache
    conditionalLog('No cached brand data found or force refresh, fetching from Firestore...');
    return await fetchAndCacheBrands();
    
  } catch (error) {
    console.error('Error getting brands:', error);
    
    // Fallback to Firestore if IndexedDB fails
    try {
      return await fetchAndCacheBrands();
    } catch (firestoreError) {
      console.error('Error fetching brands from Firestore:', firestoreError);
      return [];
    }
  }
};

/**
 * Fetches brands from Firestore and updates IndexedDB cache
 * @returns {Promise<Array>} Array of brand objects with equipment counts
 */
const fetchAndCacheBrands = async () => {
  try {
    const q = query(collection(db, 'marcas'), orderBy('nombre'));
    const querySnapshot = await getDocs(q);
    let brandsList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate equipment counts
    brandsList = await calculateBrandEquipmentCounts(brandsList);
    
    // Update IndexedDB cache with fresh data
    await syncBrandsToCache(brandsList);
    
    // Record cache sync time
    recordDataSync('brands');
    
    conditionalLog(`Fetched ${brandsList.length} brands from Firestore and updated cache`);
    return brandsList;
    
  } catch (error) {
    console.error('Error fetching brands from Firestore:', error);
    throw error;
  }
};

/**
 * Calculate equipment counts for brands by checking equipment catalog
 * @param {Array} brandsList - Array of brand objects
 * @returns {Promise<Array>} Brands with equipment counts
 */
const calculateBrandEquipmentCounts = async (brandsList) => {
  try {
    // Get equipment from cache or Firestore to count brand usage
    const { getAllEquipment } = await import('./equipmentService');
    const equipmentList = await getAllEquipment(true); // Include archived for accurate counts
    
    return brandsList.map(brand => {
      let equipmentCount = 0;
      
      equipmentList.forEach(equipment => {
        // Check by brand ID (new format) first
        if (equipment.marcaId === brand.id) {
          equipmentCount++;
        }
        // No fallback to marca field - all equipment should have marcaId
      });
      
      return {
        ...brand,
        equipmentCount
      };
    });
  } catch (error) {
    console.error('Error calculating brand equipment counts:', error);
    // Return original list if calculation fails
    return brandsList.map(brand => ({ ...brand, equipmentCount: 0 }));
  }
};

// ===== INDEXEDDB CACHE OPERATIONS =====

/**
 * Get brands from IndexedDB cache
 * @returns {Promise<Array|null>} Cached brands or null if not found
 */
const getBrandsFromCache = async () => {
  try {
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve) => {
    
      // Database already opened, proceed with transaction
      
      if (!db.objectStoreNames.contains('brands')) {
        conditionalLog('Brands store does not exist in IndexedDB');
        db.close();
        resolve(null);
        return;
      }
      
      const transaction = db.transaction(['brands'], 'readonly');
      const store = transaction.objectStore('brands');
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        db.close();
        const brands = getAllRequest.result || [];
        resolve(brands);
      };
      
      getAllRequest.onerror = () => {
        console.error('Error reading brands from IndexedDB cache');
        db.close();
        resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting brands from cache:', error);
    return null;
  }
};

/**
 * Sync brands to IndexedDB cache
 * @param {Array} brands - Array of brand objects to cache
 * @returns {Promise<boolean>} Success status
 */
const syncBrandsToCache = async (brands) => {
  try {
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve) => {
    
      // Database and stores already created, proceed with transaction
      
      // Ensure the brands store exists
      if (!db.objectStoreNames.contains('brands')) {
        conditionalLog('Brands store does not exist, will be created on next upgrade');
        db.close();
        resolve(false);
        return;
      }
      
      const transaction = db.transaction(['brands'], 'readwrite');
      const store = transaction.objectStore('brands');
      
      // Clear existing data
      const clearRequest = store.clear();
      
      clearRequest.onsuccess = () => {
        // Add all brands with serialized dates
        const serializedBrands = brands.map(brand => serializeDates(brand));
        
        let completed = 0;
        const total = serializedBrands.length;
        
        if (total === 0) {
          db.close();
          resolve(true);
          return;
        }
        
        serializedBrands.forEach(brand => {
          const addRequest = store.add(brand);
          
          addRequest.onsuccess = () => {
            completed++;
            if (completed === total) {
              db.close();
              conditionalLog(`Synced ${total} brands to IndexedDB cache`);
              resolve(true);
            }
          };
          
          addRequest.onerror = () => {
            console.error('Error adding brand to IndexedDB cache:', brand.id);
            completed++;
            if (completed === total) {
              db.close();
              resolve(false);
            }
          };
        });
      };
      
      clearRequest.onerror = () => {
        console.error('Error clearing brands from IndexedDB cache');
        db.close();
        resolve(false);
      };
    });
  } catch (error) {
    console.error('Error syncing brands to cache:', error);
    return false;
  }
};

// ===== DATE SERIALIZATION UTILITIES =====

/**
 * Serialize Firestore Timestamps to ISO strings for IndexedDB storage
 * @param {Object} brand - Brand object with potential Firestore Timestamps
 * @returns {Object} Brand object with serialized dates
 */
const serializeDates = (brand) => {
  const serialized = { ...brand };
  
  // Convert Firestore Timestamps to ISO strings
  if (serialized.createdAt?.toDate) {
    serialized.createdAt = serialized.createdAt.toDate().toISOString();
  }
  if (serialized.updatedAt?.toDate) {
    serialized.updatedAt = serialized.updatedAt.toDate().toISOString();
  }
  
  return serialized;
};

/**
 * Deserialize ISO strings back to Date objects after reading from IndexedDB
 * @param {Object} brand - Brand object with serialized dates
 * @returns {Object} Brand object with Date objects
 */
const deserializeDates = (brand) => {
  const deserialized = { ...brand };
  
  // Convert ISO strings back to Date objects
  if (typeof deserialized.createdAt === 'string') {
    deserialized.createdAt = new Date(deserialized.createdAt);
  }
  if (typeof deserialized.updatedAt === 'string') {
    deserialized.updatedAt = new Date(deserialized.updatedAt);
  }
  
  return deserialized;
};

// ===== SEARCH OPERATIONS =====

/**
 * Search brands locally from IndexedDB cache
 * @param {string} searchTerm - Search term
 * @param {Array} tiposEquipo - Array of equipment types for cross-referencing
 * @returns {Promise<Array>} Filtered brand results
 */
export const searchBrandsLocal = async (searchTerm, tiposEquipo = []) => {
  try {
    const brands = await getAllBrands();
    
    if (!searchTerm) return brands;
    
    const searchTermLower = searchTerm.toLowerCase();
    
    const getTipoNameById = (tipoId) => {
      const tipo = tiposEquipo.find(t => t.id === tipoId);
      return tipo ? tipo.nombre : 'Tipo desconocido';
    };
    
    return brands.filter(brand => {
      // Search by name
      if (brand.nombre?.toLowerCase().includes(searchTermLower)) {
        return true;
      }
      
      // Search by equipment types (new format)
      if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
        return brand.tiposEquipoIds.some(tipoId => {
          const tipoName = getTipoNameById(tipoId);
          return tipoName.toLowerCase().includes(searchTermLower);
        });
      }
      
      // Search by equipment types (old format for backward compatibility)
      if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
        return brand.tiposEquipo.some(tipo => 
          tipo.toLowerCase().includes(searchTermLower)
        );
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error searching brands locally:', error);
    return [];
  }
};

/**
 * Check if a brand is used in equipment catalog
 * @param {string} brandName - Brand name to check
 * @returns {Promise<boolean>} Whether brand is in use
 */
export const checkBrandInUse = async (brandName) => {
  try {
    const { getAllEquipment } = await import('./equipmentService');
    const equipmentList = await getAllEquipment(true); // Include archived
    
    // Get brand ID from brand name to check equipment usage
    const allBrands = await getAllBrands();
    const brand = allBrands.find(b => b.nombre === brandName);
    if (!brand) return false;
    
    return equipmentList.some(equipment => {
      return equipment.marcaId === brand.id;
    });
  } catch (error) {
    console.error('Error checking brand usage:', error);
    return true; // Assume brand is in use if there's an error
  }
};